import React, { useState } from 'react';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { toast } from './feedback';
import { db } from '../firebase';
import { blockUser } from '../blocking';

const REASONS = [
  { id: 'harassment', label: 'Harassment or threats', urgent: true },
  { id: 'safety', label: 'Safety concern', urgent: true },
  { id: 'sexual', label: 'Sexual or inappropriate content', urgent: true },
  { id: 'patient_data', label: 'Shared patient information', urgent: true },
  { id: 'copyright', label: 'Copyrighted or exam material', urgent: false },
  { id: 'spam', label: 'Spam or scam', urgent: false },
  { id: 'impersonation', label: 'Impersonation or fake profile', urgent: false },
  { id: 'other', label: 'Something else', urgent: false }
];

// onBlocked runs when the reporter also blocked the person, so the screen can hide them.
function ReportUser({ user, userProfile, reported, onClose, showNotification, onBlocked }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [sending, setSending] = useState(false);
  const [alsoBlock, setAlsoBlock] = useState(true);

  const chosen = REASONS.find(r => r.id === reason);

  const submit = async () => {
    if (!reason) { toast('Please choose a reason.', { type: 'error' }); return; }
    setSending(true);
    try {
      await addDoc(collection(db, 'reports'), {
        reporterUid: user.uid,
        reporterName: userProfile?.name || 'Unknown',
        reporterEmail: user.email || '',
        reportedUid: reported.uid,
        reportedName: reported.name || 'Unknown',
        reason,
        reasonLabel: chosen.label,
        urgent: chosen.urgent,
        details: details.trim(),
        status: 'open',
        createdAt: serverTimestamp()
      });

      if (chosen.urgent) {
        const subject = `URGENT report: ${chosen.label}`;
        const body =
          `Reported user: ${reported.name} (${reported.uid})\n` +
          `Reported by: ${userProfile?.name || 'Unknown'} (${user.uid})\n` +
          `Reason: ${chosen.label}\n\n` +
          `Details:\n${details.trim() || "(none given)"}\n`;
        window.open(
          `mailto:urgent@medxmeet.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`,
          '_blank'
        );
      }

      let blocked = false;
      if (alsoBlock) {
        try { await blockUser(user.uid, reported.uid, reported.name); blocked = true; }
        catch (e) { console.error(e); toast(`Report sent, but blocking ${reported.name} failed. Try Block again.`, { type: 'error' }); }
      }

      showNotification(
        blocked ? `Report submitted · ${reported.name} blocked` : 'Report submitted',
        chosen.urgent
          ? 'We treat this as urgent. If anyone is in immediate danger, contact emergency services.'
          : 'Thank you. We review every report.'
      );
      onClose();
      if (blocked && onBlocked) onBlocked();
    } catch (e) {
      console.error(e);
      toast('Could not submit the report. Please email support@medxmeet.com.', { type: 'error' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-head">
          <h3>Report {reported.name}</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <p className="muted" style={{fontSize:'14px',marginBottom:'20px'}}>
          Reports go only to the MedXMeet team. {reported.name} will not be told you reported them.
        </p>

        <div className="form-group">
          <label>What happened? *</label>
          <div style={{display:'grid',gap:'8px'}}>
            {REASONS.map(r => (
              <label key={r.id} className="checkbox-label">
                <input type="radio" name="reason" checked={reason===r.id} onChange={()=>setReason(r.id)} />
                {r.label}
              </label>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label>Tell us more (optional)</label>
          <textarea
            className="message-box"
            placeholder="What happened, and when? Include anything that would help us understand."
            value={details}
            onChange={e=>setDetails(e.target.value)}
            style={{minHeight:'110px'}}
          />
        </div>

        {chosen?.urgent && (
          <div className="danger-box">
            <strong>If someone is in immediate danger, contact emergency services now.</strong> MedXMeet reporting is not an emergency service.
          </div>
        )}

        <div className="form-group">
          <label className="checkbox-label">
            <input type="checkbox" checked={alsoBlock} onChange={e=>setAlsoBlock(e.target.checked)} />
            Also block {reported.name}: they can't contact you and you won't see them
          </label>
        </div>

        <button className="btn full" onClick={submit} disabled={sending}>
          {sending ? 'Submitting…' : 'Submit report'}
        </button>
      </div>
    </div>
  );
}

export default ReportUser;